// ============================================================================
// LE COMPTE D'ÉCARTS OUVERTS DU RAIL — la pastille rouge de « Centre d'écarts ».
//
// UTILITÉ : un écart attend un arbitrage humain ; le rail doit dire QU'IL Y EN A sans qu'on ouvre la
// page (c'est ce que la maquette dessine). Le compte est celui, MESURÉ, de `/variances/resume` —
// le même que la tuile du tableau de bord, jamais un second calcul qui divergerait.
//
// ⚠️ UN COMPTEUR QUI NE SE RAFRAÎCHIT PAS EST PIRE QUE PAS DE COMPTEUR : après avoir justifié le
// dernier écart, un « 3 » qui reste collé au rail dit qu'il reste du travail qui n'existe plus
// (§23.0). D'où le minuscule bus ci-dessous : l'écran des écarts SIGNALE, le rail RECOMPTE. On ne
// décrémente jamais localement — plusieurs écarts peuvent tomber d'un coup, et un collègue peut en
// créer pendant qu'on lit.
//
// ⚠️ ÉCHEC DE LECTURE = PAS DE PASTILLE, jamais un zéro : « 0 » affirme « aucun écart », alors qu'on
// ne sait rien. L'absence, elle, n'affirme rien.
// ============================================================================
import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * LA PASTILLE SE MONTRE-T-ELLE ? Trois refus, un seul oui — la règle vaut d'être nommée et testée
 * plutôt qu'écrite dans un `&&` du JSX :
 *  · `null` (chargement, échec de lecture, droit absent) → RIEN. Un « 0 » affirmerait « aucun écart »
 *    alors qu'on ne sait pas ; l'absence, elle, n'affirme rien.
 *  · `0` → RIEN. Une pastille grise à zéro attire l'œil sur un travail qui n'existe pas.
 *  · un nombre > 0 → la pastille, avec ce nombre.
 */
export function pastilleEcartsVisible(compte: number | null): compte is number {
  return compte !== null && compte > 0;
}

/** Les abonnés au recomptage — le rail, en pratique ; la liste reste ouverte. */
const abonnes = new Set<() => void>();

/**
 * À APPELER APRÈS TOUTE ÉCRITURE QUI CHANGE LE NOMBRE D'ÉCARTS OUVERTS (justification, correction
 * de stock négatif, arbitrage de rétrocession). Sans cet appel, la pastille reste sur un chiffre
 * périmé jusqu'au prochain rechargement de page.
 */
export function signalerEcartsChanges(): void {
  for (const f of abonnes) f();
}

/**
 * Le nombre d'écarts OUVERTS, ou `null` tant qu'on ne sait pas (chargement, échec, droit absent).
 * `null` et `0` sont deux choses différentes, et l'appelant doit pouvoir les distinguer.
 */
export function useCompteEcarts(): number | null {
  const [compte, setCompte] = useState<number | null>(null);

  useEffect(() => {
    let vivant = true;
    const read = () => {
      api.variancesResume()
        .then((r) => { if (vivant) setCompte(r.open); })
        // ⚠️ Silencieux À DESSEIN : le rail n'est pas l'endroit où l'on apprend qu'une lecture a
        //    échoué (le Centre d'écarts, lui, le dit avec son motif). Ici, on retire la pastille.
        .catch(() => { if (vivant) setCompte(null); });
    };
    read();
    abonnes.add(read);
    return () => { vivant = false; abonnes.delete(read); };
  }, []);

  return compte;
}
